import { ErrorNotice } from '../../components/ErrorNotice.js';
import { useEffect, useMemo, useState } from 'react';
import {
  listObservations,
  listSubjects,
  type ObservationSummary,
  type Subject,
} from '../../lib/api/endpoints.js';
import { subjectDisplayName } from '../../lib/subjects.js';
import { computeStamps, useObservationDigest, type ObservationDigest } from '../../lib/standing.js';
import { PlaybackButton } from '../../components/PlaybackButton.js';

interface Props {
  subjectId?: string;
}

/**
 * The give-back layer.
 *
 * The implementation plan is blunt about why this exists: without something
 * returned to the owner, week-two usage goes to zero regardless of how good the
 * capture flow is. People do not keep contributing to a corpus out of altruism;
 * they keep contributing because they are learning something about their own
 * animal.
 *
 * What is honest to show at this stage is narrow: what was recorded, when, what
 * triggered it, and what happened next. Patterns across records need more data
 * than any single contributor has, and dressing up thin counts as insight —
 * "your dog is 40% more anxious this week" — would be inventing findings. The
 * timeline shows the contributor their own observations back, well organised,
 * and claims nothing beyond that.
 */
export function TimelinePage({ subjectId }: Props) {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [observations, setObservations] = useState<ObservationSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  // For the Field record teaser only — independent of `observations` above,
  // which is scoped to the subject being viewed. Best-effort: the teaser just
  // doesn't render if this fails, never a blocked timeline.
  const { digest } = useObservationDigest();

  useEffect(() => {
    listSubjects().then(setSubjects).catch(() => undefined);
  }, []);

  useEffect(() => {
    setObservations(null);
    setError(null);
    listObservations({ ...(subjectId ? { subjectId } : {}), limit: 50 })
      .then((page) => setObservations(page.items))
      .catch(setError);
  }, [subjectId, attempt]);

  const subject = subjects.find((s) => s.id === subjectId);

  const counts = useMemo(() => {
    if (!observations) return [];
    const tally = new Map<string, number>();
    for (const observation of observations) {
      for (const signal of signalsOf(observation)) {
        tally.set(signal, (tally.get(signal) ?? 0) + 1);
      }
    }
    return [...tally.entries()].sort((a, b) => b[1] - a[1]);
  }, [observations]);

  if (error !== null) {
    return (
      <ErrorNotice
        error={error}
        title="We couldn't load your timeline."
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }
  if (observations === null) return <p className="meta">Loading…</p>;

  if (observations.length === 0) {
    return (
      <div className="empty">
        <h2>Nothing here yet</h2>
        <p>
          Once you contribute a recording it appears here, with what triggered it and what happened
          next.
        </p>
        <a className="btn" href="#/contribute">
          Contribute a recording
        </a>
      </div>
    );
  }

  return (
    <div>
      <p className="eyebrow">Timeline</p>
      <h1 className="page-title">
        {subject ? (subject.name ?? subject.commonName ?? subject.scientificName) : 'Everything you have recorded'}
      </h1>
      <p className="page-intro">
        {observations.length} observation{observations.length === 1 ? '' : 's'}.
      </p>

      {digest && <StandingTeaser digest={digest} subject={subject} />}

      {counts.length > 0 && (
        <section className="panel" style={{ marginBottom: '2rem' }}>
          <p className="eyebrow" style={{ marginBottom: '0.9rem' }}>
            What you've recorded most
          </p>
          <ul className="rows">
            {counts.slice(0, 6).map(([signal, count]) => (
              <li key={signal}>
                <span>{humanise(signal)}</span>
                <span className="meta">{count}</span>
              </li>
            ))}
          </ul>
          <p className="field-hint" style={{ marginTop: '0.9rem' }}>
            Counts only. Anything more — whether a pattern is real, whether it is changing — needs
            far more data than one animal provides, and we would rather show you nothing than
            invent a finding.
          </p>
        </section>
      )}

      <ul className="rows">
        {observations.map((observation) => {
          const assetId = mediaAssetIdOf(observation);
          return (
            <li key={observation.id}>
              <div style={{ flex: 1, minWidth: '16rem' }}>
                <strong>{humanise(signalsOf(observation)[0] ?? 'Recording')}</strong>
                <div style={{ marginTop: '0.35rem', fontSize: '0.9rem', color: 'var(--ink-2)' }}>
                  <span style={{ color: 'var(--context)' }}>after</span>{' '}
                  {antecedentOf(observation)}
                  {' → '}
                  <span style={{ color: 'var(--context)' }}>then</span> {consequenceOf(observation)}
                </div>
                <div className="meta" style={{ marginTop: '0.35rem' }}>
                  {new Date(observation.createdAt).toLocaleString()} · {observation.verificationStatus}
                  {observation.supersededBy ? ' · corrected' : ''}
                </div>
              </div>
              {assetId && <PlaybackButton assetId={assetId} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The timeline's one link into Field record (`#/record`) — see that page for
 * why standing lives on its own route rather than here. Renders nothing it
 * can't back with a real count: with a specific animal in view it names them
 * and their own count; without one it speaks only in totals.
 */
function StandingTeaser({
  digest,
  subject,
}: {
  digest: ObservationDigest;
  subject?: Subject | undefined;
}) {
  const stamps = computeStamps(digest);
  const earned = stamps.filter((s) => s.on).length;
  const name = subject ? subjectDisplayName(subject) : null;
  const subjectCount = subject ? (digest.bySubject[subject.id] ?? 0) : null;

  return (
    <button type="button" className="standing-teaser" onClick={() => (location.hash = '#/record')}>
      <span className="standing-teaser-body">
        {name && subjectCount !== null
          ? `${subjectCount} recording${subjectCount === 1 ? '' : 's'} of ${name}, part of ${digest.total} you have contributed.`
          : `${digest.total} recording${digest.total === 1 ? '' : 's'} you have contributed.`}
        <span className="standing-teaser-stamps">
          {earned} of {stamps.length} stamps collected
        </span>
      </span>
      <span className="standing-teaser-go" aria-hidden="true">
        Your record →
      </span>
    </button>
  );
}

/* ── Reading FaunaCore records ──────────────────────────────────────────────
   The API returns the record exactly as stored. These readers are deliberately
   defensive: a record written against an older schema or ethogram version must
   still render, because the corpus keeps records forever and the app does not
   get to assume everything was written by the current build. */

type Record_ = Record<string, unknown>;

function signalsOf(observation: ObservationSummary): string[] {
  const segments = (observation.record as Record_).segments;
  if (!Array.isArray(segments)) return [];
  return segments.flatMap((segment) => {
    const annotations = (segment as Record_).annotations;
    if (!Array.isArray(annotations)) return [];
    return annotations
      .map((a) => (a as Record_).signal_type)
      .filter((s): s is string => typeof s === 'string');
  });
}

/** The recording this observation is about — null for a record with no media reference. */
function mediaAssetIdOf(observation: ObservationSummary): string | null {
  const media = (observation.record as Record_).media;
  if (!Array.isArray(media) || media.length === 0) return null;
  const assetId = (media[0] as Record_).asset_id;
  return typeof assetId === 'string' ? assetId : null;
}

function antecedentOf(observation: ObservationSummary): string {
  const antecedent = (observation.record as Record_).antecedent as Record_ | undefined;
  if (!antecedent) return 'something unrecorded';

  const free = antecedent.description_free;
  if (typeof free === 'string' && free.trim()) return free.trim();

  const coded = antecedent.coded_terms;
  if (Array.isArray(coded) && coded.length > 0) {
    return coded.map((t) => humanise(String(t)).toLowerCase()).join(', ');
  }

  return 'something unrecorded';
}

function consequenceOf(observation: ObservationSummary): string {
  const consequence = (observation.record as Record_).consequence as Record_ | undefined;
  const action = consequence?.animal_next_action;
  return typeof action === 'string' && action.trim() ? action.trim() : 'unrecorded';
}

function humanise(term: string): string {
  const tail = term.split('.').slice(1).join(' ') || term;
  const words = tail.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
